import React, { useState } from "react";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useT } from "../i18n/I18nProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useT();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t("contact.err_empty"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("contact.ok"));
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("contact.err_fail"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-28 bg-[#14532d] relative overflow-hidden"
    >
      <div
        className="absolute -top-32 -right-32 w-[460px] h-[460px] rounded-full opacity-25 el-slow-spin"
        style={{ background: "conic-gradient(from 0deg, #b8420f, #0f3d21, #b8420f, #0f3d21, #b8420f)" }}
        aria-hidden="true"
      />
      <div className="relative max-w-6xl mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-10 md:gap-14 items-start">
        <div className="md:col-span-5 text-white">
          <div className="text-[12px] uppercase tracking-[0.22em] text-orange-300 font-bold mb-3">
            {t("contact.eyebrow")}
          </div>
          <h2 className="font-display font-black text-[40px] md:text-[52px] leading-[1.05] tracking-tight">
            {t("contact.h_a")}{" "}
            <span className="font-script text-orange-300 text-[60px] md:text-[78px] leading-none">
              {t("contact.h_b")}
            </span>{" "}
            <span className="italic">{t("contact.h_c")}</span>
          </h2>
          <p className="mt-6 text-[17px] text-emerald-100 leading-relaxed max-w-md">
            {t("contact.sub")}
          </p>

          <div className="mt-9 space-y-3 text-[15px]">
            <a href="tel:+18176561046" data-testid="contact-call-link" className="flex items-center gap-3 text-emerald-100 hover:text-white transition-colors">
              <span className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.93.37 1.84.7 2.71a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.37-1.27a2 2 0 0 1 2.11-.45c.87.33 1.78.57 2.71.7A2 2 0 0 1 22 16.92z"/></svg>
              </span>
              (817) 656-1046
            </a>
            <a href="mailto:elsolraul@yahoo.com" data-testid="contact-email-link" className="flex items-center gap-3 text-emerald-100 hover:text-white transition-colors">
              <span className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></svg>
              </span>
              elsolraul@yahoo.com
            </a>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="md:col-span-7 bg-[#fdfbf7] rounded-[28px] p-6 md:p-9 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45)]"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="name" className="text-stone-900 font-semibold text-[13px] mb-1.5 inline-block">{t("contact.l_name")}</Label>
              <Input id="name" name="name" value={form.name} onChange={onChange} placeholder={t("contact.ph_name")} data-testid="contact-name-input" className="h-12 bg-white border-stone-300 focus-visible:ring-[#b8420f] text-stone-900" required />
            </div>
            <div>
              <Label htmlFor="email" className="text-stone-900 font-semibold text-[13px] mb-1.5 inline-block">{t("contact.l_email")}</Label>
              <Input id="email" name="email" type="email" value={form.email} onChange={onChange} placeholder={t("contact.ph_email")} data-testid="contact-email-input" className="h-12 bg-white border-stone-300 focus-visible:ring-[#b8420f] text-stone-900" required />
            </div>
            <div>
              <Label htmlFor="phone" className="text-stone-900 font-semibold text-[13px] mb-1.5 inline-block">{t("contact.l_phone")}</Label>
              <Input id="phone" name="phone" value={form.phone} onChange={onChange} placeholder={t("contact.ph_phone")} data-testid="contact-phone-input" className="h-12 bg-white border-stone-300 focus-visible:ring-[#b8420f] text-stone-900" />
            </div>
            <div>
              <Label htmlFor="subject" className="text-stone-900 font-semibold text-[13px] mb-1.5 inline-block">{t("contact.l_subject")}</Label>
              <Input id="subject" name="subject" value={form.subject} onChange={onChange} placeholder={t("contact.ph_subject")} data-testid="contact-subject-input" className="h-12 bg-white border-stone-300 focus-visible:ring-[#b8420f] text-stone-900" />
            </div>
          </div>
          <div className="mt-5">
            <Label htmlFor="message" className="text-stone-900 font-semibold text-[13px] mb-1.5 inline-block">{t("contact.l_message")}</Label>
            <Textarea id="message" name="message" value={form.message} onChange={onChange} rows={5} placeholder={t("contact.ph_message")} data-testid="contact-message-input" className="bg-white border-stone-300 focus-visible:ring-[#b8420f] text-stone-900 resize-none" required />
          </div>
          <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="text-[12px] text-stone-500">{t("contact.privacy")}</div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-button"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#b8420f] hover:bg-[#8c2f08] disabled:opacity-60 disabled:cursor-not-allowed text-white px-7 py-3.5 text-[14px] font-bold tracking-wide transition-colors shadow-[0_10px_30px_-10px_rgba(184,66,15,0.5)]"
            >
              {submitting ? t("contact.btn_sending") : t("contact.btn_send")}
              {!submitting && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
